import { useCallback, useEffect, useMemo } from 'react';
import type {
  AutosaveState,
  QueueAutosaveOptions,
  UseEntityDraftAutosaveOptions,
  UseEntityDraftAutosaveResult,
} from '#types/types';

export const DEFAULT_AUTOSAVE_STATE: AutosaveState = {
  dirty: false,
  saveState: 'idle',
  lastDraftSignature: null,
  lastPersistedSignature: null,
  retryCount: 0,
  lastError: null,
  updatedAt: 0,
};

const stableStringify = (value: unknown): string => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (Array.isArray(value)) return `[${value.map((entry) => stableStringify(entry)).join(',')}]`;
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entry]) => entry !== undefined)
      .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
      .map(([key, entry]) => `${JSON.stringify(key)}:${stableStringify(entry)}`);
    return `{${entries.join(',')}}`;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return JSON.stringify(String(value));
  }
  return JSON.stringify(value);
};

const isSavingState = (saveState: AutosaveState['saveState']): boolean =>
  saveState === 'queued' || saveState === 'saving';

export const useEntityDraftAutosave = <
  TServer,
  TDraft,
  TPayload = Partial<TServer>,
>({
  kernel,
  entityType,
  entityId,
  scope,
  serverData,
  draft,
  setDraft,
  adapter,
  reason,
  debounceMs,
  validate,
  isDraftInitialized,
  initializeDraft,
  persist,
  onSuccess,
}: UseEntityDraftAutosaveOptions<TServer, TDraft, TPayload>): UseEntityDraftAutosaveResult => {
  const normalizedEntityId = entityId.trim();
  const serializeDraft = useMemo(() => {
    const custom = adapter.serialize as ((draft: TDraft) => string) | undefined;
    return custom ?? ((value: TDraft) => stableStringify(value));
  }, [adapter.serialize]);

  const serverDraft = useMemo(
    () => (serverData ? adapter.mapToDraft(serverData) : null),
    [adapter, serverData]
  );
  const serverSignature = useMemo(
    () => (serverDraft ? serializeDraft(serverDraft) : null),
    [serializeDraft, serverDraft]
  );
  const draftSignature = useMemo(() => serializeDraft(draft), [draft, serializeDraft]);

  const readAutosaveState = useCallback((): AutosaveState => {
    const k = kernel.current;
    if (!k || !entityType || normalizedEntityId.length === 0) return DEFAULT_AUTOSAVE_STATE;
    return k.getAutosaveState(entityType, normalizedEntityId, scope);
  }, [entityType, kernel, normalizedEntityId, scope]);

  useEffect(() => {
    const k = kernel.current;
    if (!k || !serverDraft || !entityType || normalizedEntityId.length === 0) return;

    const autosaveState = k.getAutosaveState(entityType, normalizedEntityId, scope);
    const baselineSignature = autosaveState.lastPersistedSignature ?? '';
    const hasBaseline = baselineSignature.length > 0;
    const hasUnsavedLocalDraft =
      (hasBaseline && draftSignature !== baselineSignature) ||
      autosaveState.dirty ||
      isSavingState(autosaveState.saveState);

    if (isDraftInitialized === false) {
      const applyDraft = initializeDraft ?? setDraft;
      if (!hasUnsavedLocalDraft && serverSignature && serverSignature !== draftSignature) {
        applyDraft(serverDraft);
      }
      if (serverSignature && autosaveState.lastPersistedSignature !== serverSignature) {
        k.setAutosaveBaseline(entityType, normalizedEntityId, serverSignature, { scope });
      }
      return;
    }

    if (!hasUnsavedLocalDraft && serverSignature && serverSignature !== draftSignature) {
      setDraft(serverDraft);
    }

    if (!hasUnsavedLocalDraft && serverSignature && autosaveState.lastPersistedSignature !== serverSignature) {
      k.setAutosaveBaseline(entityType, normalizedEntityId, serverSignature, { scope });
    }
  }, [
    draftSignature,
    entityType,
    initializeDraft,
    isDraftInitialized,
    kernel,
    normalizedEntityId,
    scope,
    serverDraft,
    serverSignature,
    setDraft,
  ]);

  useEffect(() => {
    const k = kernel.current;
    if (!k || !serverDraft || !entityType || normalizedEntityId.length === 0) return;
    if (isDraftInitialized === false) return;

    const autosaveState = k.getAutosaveState(entityType, normalizedEntityId, scope);
    const baselineSignature = autosaveState.lastPersistedSignature ?? '';

    if (baselineSignature.length > 0 && draftSignature === baselineSignature) {
      k.settleAutosave(entityType, normalizedEntityId, {
        scope,
        signature: draftSignature,
        saveState: 'idle',
      });
      return;
    }

    if (serverSignature && serverSignature === draftSignature && !isSavingState(autosaveState.saveState)) {
      if (autosaveState.lastPersistedSignature !== serverSignature) {
        k.setAutosaveBaseline(entityType, normalizedEntityId, serverSignature, { scope });
      }
      return;
    }

    if (validate && !validate(draft)) {
      k.cancelAutosave(entityType, normalizedEntityId, scope);
      return;
    }

    const payload = adapter.mapToPayload(draft);
    k.queueAutosave<TPayload>({
      entityType,
      entityId: normalizedEntityId,
      scope,
      reason,
      debounceMs,
      buildPayload: () => payload,
      serializePayload: () => draftSignature,
      persist: async (
        queuedPayload,
        context: Parameters<QueueAutosaveOptions<TPayload>['persist']>[1]
      ) => {
        const persistResult = await persist(queuedPayload, context);
        if (persistResult === false) return false;
        if (onSuccess) {
          await onSuccess(queuedPayload, context);
        }
        return persistResult;
      },
    });
  }, [
    adapter,
    debounceMs,
    draft,
    draftSignature,
    entityType,
    isDraftInitialized,
    kernel,
    normalizedEntityId,
    onSuccess,
    persist,
    reason,
    scope,
    serverDraft,
    serverSignature,
    validate,
  ]);

  const autosaveState = readAutosaveState();
  return {
    autosaveState,
    draftSignature,
    serverSignature,
  };
};
